import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Section } from '@/components/Landing/LandingKit';
import { fluid, isWeb } from '@/components/Landing/landingTokens';
import { BILLING, PREMIUM_BILLING, PLANS, HERO_TRUST } from '@/constants/pricingContent';
import { PricingButton, PricingFeature, usePricing } from './PricingKit';

// Select from the billing configuration; the expanded view retains every feature.
const SUMMARY_FEATURES = { free: [0, 2, 3, 4, 5], plus: [1, 2, 3, 4, 5], 'plus-premium': [0, 1, 2, 3, 4] };

function PlanColumn({ plan, isAnnual, expanded, onSelect }) {
  const { c, isCompact, isXS } = usePricing();
  const featured = Boolean(plan.highlight);
  const billing = plan.id === 'plus' ? BILLING : PREMIUM_BILLING;
  const free = plan.id === 'free';
  const price = free ? plan.price : `$${isAnnual ? billing.annualPerMonth : billing.monthly}`;
  const billingNote = free ? 'No subscription required' : `${isAnnual ? `$${billing.annualTotal} billed annually` : 'Billed monthly'}`;
  const features = expanded ? plan.features : SUMMARY_FEATURES[plan.id].map(index => plan.features[index]);

  return (
    <View style={{ flexGrow: isCompact ? 0 : 1, flexBasis: isCompact ? 'auto' : 0, minWidth: 0, borderRadius: 20, borderWidth: 1, borderColor: featured ? c.ink : c.rule, backgroundColor: featured ? c.panel : c.cardSolid, padding: isXS ? 24 : 28 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 24, marginBottom: 20 }}>
        <Text weight="medium" style={{ color: c.muted, fontSize: 12 }}>{free ? 'A first look' : featured ? 'For every day' : 'For bigger workloads'}</Text>
        {featured && <View style={{ borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4, backgroundColor: c.ink }}><Text weight="medium" style={{ color: c.page, fontSize: 11 }}>Recommended</Text></View>}
      </View>
      <Text tag="h2" weight="bold" style={{ color: c.ink, fontSize: 27, lineHeight: 34, letterSpacing: -0.8 }}>{plan.name}</Text>
      <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22, marginTop: 8 }}>{plan.tagline}</Text>
      <View style={{ marginTop: 28, marginBottom: 24 }}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <Text weight="bold" style={{ color: c.ink, fontSize: 46, lineHeight: 54, letterSpacing: -2, fontVariant: ['tabular-nums'] }}>{price}</Text>
          <Text style={{ color: c.muted, fontSize: 14 }}>{free ? 'to start' : '/ month'}</Text>
        </View>
        <Text style={{ color: c.muted, fontSize: 12, lineHeight: 20, marginTop: 8 }}>{billingNote}</Text>
      </View>
      <PricingButton label={plan.cta} href={plan.href} onPress={onSelect} secondary={!featured} full />
      <View style={{ marginTop: 28, paddingTop: 24, borderTopWidth: 1, borderTopColor: c.rule, gap: 14 }}>
        <Text weight="medium" style={{ color: c.ink, fontSize: 13, marginBottom: 2 }}>{free ? 'Get a feel for Gab AI' : featured ? 'Your everyday AI workspace' : 'Everything in Plus, with more'}</Text>
        {features.map(feature => <PricingFeature key={feature.name} feature={feature} />)}
      </View>
    </View>
  );
}

function PricingPlans({ isAnnual, onSelectPlan }) {
  const { c, isCompact, width } = usePricing();
  const [expanded, setExpanded] = useState(false);
  return (
    <Section label="Plans" max={1200} top={false} contentStyle={{ paddingBottom: fluid(width, 48, 64) }}>
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 16, alignItems: 'stretch' }}>
        {PLANS.map(plan => <PlanColumn key={plan.id} plan={plan} isAnnual={isAnnual} expanded={expanded} onSelect={onSelectPlan ? () => onSelectPlan(plan) : undefined} />)}
      </View>
      <View style={{ alignItems: 'center', marginTop: 24, gap: 16 }}>
        <Pressable accessibilityRole="button" accessibilityLabel={expanded ? 'Show plan highlights' : 'Show all plan features'} accessibilityState={{ expanded }} {...(isWeb ? { 'aria-expanded': expanded } : {})} onPress={() => setExpanded(value => !value)} style={{ minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: 8, ...(isWeb ? { cursor: 'pointer' } : {}) }}>
          <Text weight="medium" style={{ color: c.ink, fontSize: 14, textDecorationLine: 'underline' }}>{expanded ? 'Show plan highlights' : 'Show all plan features'}</Text>
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={10} color={c.ink} />
        </Pressable>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', columnGap: 24, rowGap: 12 }}>
          {HERO_TRUST.map(item => <View key={item} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}><Icon name="check" size={10} color={c.muted} /><Text style={{ color: c.muted, fontSize: 12 }}>{item}</Text></View>)}
        </View>
      </View>
    </Section>
  );
}

export default React.memo(PricingPlans);
